import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { User, LoginResponse } from '../interfaces/user.interface';
import { logger } from '../utils/logger';
import { Permission } from '../permissions/permission.enum';
import { RolePermissions } from '../permissions/role-permissions';
import { UserRole } from '../enums/user-role.enum';

interface AuthContextType {
  user: User | null;
  login: (loginResponse: LoginResponse) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
}

const missingAuthProviderError = () =>
  new Error("AuthContext must be used within an AuthProvider");

const defaultLogin = async () => {
  throw missingAuthProviderError();
};

const defaultLogout = () => {
  throw missingAuthProviderError();
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  login: defaultLogin,
  logout: defaultLogout,
  loading: true,
  isAuthenticated: false,
  permissions: [],
  hasPermission: () => false,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token/session on mount
    const initAuth = async () => {
      try {
        const user = await authService.getProfile({ skipAuthRedirect: true });
        setUser(user);
      } catch {
        // Not authenticated, ignore error
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = async (loginResponse: LoginResponse) => {
    // Expecting { access_token, ...user }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { access_token, ...userData } = loginResponse;
    // Token is stored in cookie by backend
    setUser(userData as unknown as User);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      logger.error('Logout failed:', error);
    }
    setUser(null);
    window.location.href = '/login';
  };

  const permissions = (() => {
    const direct = user?.permissions as Permission[] | undefined;
    if (Array.isArray(direct) && direct.length > 0) return direct;

    if (user && (user.role === UserRole.ADMIN || user.role === UserRole.USER)) {
      return RolePermissions[user.role as UserRole];
    }

    return [];
  })();

  const hasPermission = (permission: Permission) => {
    return permissions.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        isAuthenticated: !!user,
        permissions,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
